/**
 * Generation content-type catalog (Block 2026-08-02, Generation foundation).
 *
 * Spec ref: docs/PRODUCT_SPEC_WORKFLOW_MATCHING_TRANSLATION_GENERATION_V1.md §16
 * (owner-approved Generation v2 page structure) + ТЗ 2026-08-02 (Text/SEO/Custom
 * grouping).
 *
 * Runtime wiring (agreed variant A with Janni 2026-08-02):
 *   - BE `_MODULE_REGISTRY` registers only `product_description` (+ search).
 *   - The UI exposes all Text/SEO/Custom types with real settings panels.
 *   - Settings for every configured type are REALLY sent in the payload
 *     (`content_type_configs`) and persisted into pipeline_config — they are
 *     not dead controls.
 *   - Only `product_description` is executed by the runtime today; other types
 *     carry an honest "runtime: soon" badge. Their settings are captured so
 *     they take effect the moment their runtime lands.
 *
 * `runtimeEnabled = true` means a BE module exists and will run for this type.
 * `settingsVariant` selects which control set the right panel renders.
 */
import type { ContentConfig } from '@/store/generationStore';

export type ContentGroup = 'text' | 'seo' | 'custom';
export type SettingsVariant = 'text' | 'seo' | 'custom';

export interface ContentTypeDef {
  key: string;
  group: ContentGroup;
  /** i18n key under `generation.types.<key>`. */
  labelKey: string;
  defaultLabel: string;
  /** Whether a BE module is registered and will actually execute. */
  runtimeEnabled: boolean;
  /** Which settings control set the right panel renders. */
  settingsVariant: SettingsVariant;
  defaultConfig: ContentConfig;
}

const textDefault: ContentConfig = {
  enabled: true,
  length: 600,
  style: '',
  prompt: '',
  additionalPrompt: '',
  inputNotes: '',
  negativePrompt: '',
  temperature: 0.7,
  repetition: 0,
  academicity: 0.5,
  outputFormat: 'plain',
};

const seoDefault: ContentConfig = {
  enabled: false,
  faqCount: 5,
  sourceBasis: 'facts',
  length: 160,
  temperature: 0.5,
};

const customDefault: ContentConfig = {
  enabled: false,
  customName: '',
  customPrompt: '',
  length: 800,
  temperature: 0.7,
  outputFormat: 'plain',
};

export const CONTENT_TYPES: ContentTypeDef[] = [
  // --- Text ---
  {
    key: 'product_description',
    group: 'text',
    labelKey: 'generation.types.product_description',
    defaultLabel: 'Product description',
    runtimeEnabled: true,
    settingsVariant: 'text',
    defaultConfig: { ...textDefault, outputColumnName: 'description' },
  },
  {
    key: 'product_card',
    group: 'text',
    labelKey: 'generation.types.product_card',
    defaultLabel: 'Product card',
    runtimeEnabled: false,
    settingsVariant: 'text',
    defaultConfig: { ...textDefault, outputColumnName: 'product_card' },
  },
  {
    key: 'article',
    group: 'text',
    labelKey: 'generation.types.article',
    defaultLabel: 'Article',
    runtimeEnabled: false,
    settingsVariant: 'text',
    defaultConfig: { ...textDefault, length: 2000, outputColumnName: 'article' },
  },
  {
    key: 'landing_page',
    group: 'text',
    labelKey: 'generation.types.landing_page',
    defaultLabel: 'Landing page',
    runtimeEnabled: false,
    settingsVariant: 'text',
    defaultConfig: { ...textDefault, length: 1500, outputFormat: 'html', outputColumnName: 'landing_page' },
  },
  // --- SEO ---
  {
    key: 'seo_title',
    group: 'seo',
    labelKey: 'generation.types.seo_title',
    defaultLabel: 'SEO title',
    runtimeEnabled: false,
    settingsVariant: 'seo',
    defaultConfig: { ...seoDefault, length: 70 },
  },
  {
    key: 'seo_description',
    group: 'seo',
    labelKey: 'generation.types.seo_description',
    defaultLabel: 'SEO description',
    runtimeEnabled: false,
    settingsVariant: 'seo',
    defaultConfig: { ...seoDefault, length: 160 },
  },
  {
    key: 'image_alt',
    group: 'seo',
    labelKey: 'generation.types.image_alt',
    defaultLabel: 'Image alt',
    runtimeEnabled: false,
    settingsVariant: 'seo',
    defaultConfig: { ...seoDefault, length: 125 },
  },
  {
    key: 'image_title',
    group: 'seo',
    labelKey: 'generation.types.image_title',
    defaultLabel: 'Image title',
    runtimeEnabled: false,
    settingsVariant: 'seo',
    defaultConfig: { ...seoDefault, length: 70 },
  },
  {
    key: 'faq',
    group: 'seo',
    labelKey: 'generation.types.faq',
    defaultLabel: 'FAQ',
    runtimeEnabled: false,
    settingsVariant: 'seo',
    defaultConfig: { ...seoDefault, faqCount: 5 },
  },
  // --- Custom ---
  {
    key: 'custom_text',
    group: 'custom',
    labelKey: 'generation.types.custom_text',
    defaultLabel: 'Custom field',
    runtimeEnabled: false,
    settingsVariant: 'custom',
    defaultConfig: { ...customDefault },
  },
];

export const CONTENT_TYPE_MAP: Record<string, ContentTypeDef> = Object.fromEntries(
  CONTENT_TYPES.map((t) => [t.key, t]),
);

export const CONTENT_GROUPS: { key: ContentGroup; labelKey: string; defaultLabel: string }[] = [
  { key: 'text', labelKey: 'generation.groups.text', defaultLabel: 'Text' },
  { key: 'seo', labelKey: 'generation.groups.seo', defaultLabel: 'SEO' },
  { key: 'custom', labelKey: 'generation.groups.custom', defaultLabel: 'Custom' },
];

/** Types whose runtime is registered in BE `_MODULE_REGISTRY` (sent as module_keys). */
export const RUNTIME_ENABLED_KEYS = CONTENT_TYPES.filter((t) => t.runtimeEnabled).map((t) => t.key);

/** Default selected type when the page first loads. */
export const DEFAULT_CONTENT_TYPE = 'product_description';

/** Build the initial contentConfigs map from the catalog defaults. */
export function buildDefaultContentConfigs(): Record<string, ContentConfig> {
  return Object.fromEntries(
    CONTENT_TYPES.map((t) => [t.key, { ...t.defaultConfig }]),
  );
}